import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Briefcase, Lock, Plane, TrendingUp, Heart, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Services = () => {
  const { t, language } = useLanguage();

  const seo = {
    fr: {
      title: "Services JSX-conseils et Henry & Gaby – Aviation privée, hospitalité exclusive & transactions confidentielles",
      description: "Découvrez les services Henry & Gaby : aviation privée, hospitalité sur-mesure, accès VIP aux grands événements et accompagnement dans les transactions stratégiques internationales.",
      h1: "Services Henry & Gaby"
    },
    en: {
      title: "JSX-conseils and Henry & Gaby Services – Private Aviation, Exclusive Hospitality & Confidential Transactions",
      description: "Discover Henry & Gaby services: private aviation, bespoke hospitality, VIP access to major events and support in international strategic transactions.",
      h1: "Henry & Gaby Services"
    }
  };

  const currentSeo = seo[language] || seo.fr;

  const services = [
    {
      icon: Briefcase,
      title: t.services.service1,
      description: t.services.service1Desc
    },
    {
      icon: Lock,
      title: t.services.service2,
      description: t.services.service2Desc
    },
    {
      icon: Plane,
      title: t.services.service3,
      description: t.services.service3Desc
    },
    {
      icon: TrendingUp,
      title: t.services.service4,
      description: t.services.service4Desc
    },
    {
      icon: Heart,
      title: t.services.service5,
      description: t.services.service5Desc
    },
    {
      icon: Users,
      title: t.services.service6,
      description: t.services.service6Desc
    }
  ];

  return (
    <div>
      <Helmet>
        <title>{currentSeo.title}</title>
        <meta name="description" content={currentSeo.description} />
        <link rel="canonical" href="https://henry-and-gaby.com/services" />
        <meta property="og:title" content={currentSeo.title} />
        <meta property="og:description" content={currentSeo.description} />
        <meta property="og:url" content="https://henry-and-gaby.com/services" />
      </Helmet>

      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid-two-column">
            <div>
              <h1 className="hero-medium" style={{ marginBottom: '24px' }}>
                {currentSeo.h1}
              </h1>
              <p className="body-large" style={{
                color: 'var(--text-secondary)',
                marginBottom: '32px'
              }}>
                {t.services.subtitle}
              </p>
              <div style={{
                padding: '32px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-light)'
              }}>
                <p className="heading-3" style={{
                  fontStyle: 'italic',
                  color: 'var(--text-secondary)',
                  marginBottom: '24px'
                }}>
                  "{t.services.quote}"
                </p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Lock size={16} color="var(--text-secondary)" />
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      {t.services.confidentiality}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={16} color="var(--text-secondary)" />
                    <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      {t.services.network}
                    </span>
                  </div>
                </div>
                
                {/* Logo JSX Conseils */}
                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_elite-network-3/artifacts/c3fgw095_Logo_JSX.png"
                    alt="Logo JSX Conseils"
                    style={{
                      maxWidth: '180px',
                      height: 'auto'
                    }}
                  />
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/hklq9p0x_JSX%20CONSEILS%20lg%202.jpg"
                alt="Strategic diplomatic consulting"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  minHeight: '400px'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px'
          }}>
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="hover-lift"
                  style={{
                    padding: '32px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-light)',
                    cursor: 'default'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    border: '1px solid var(--border-light)',
                    background: 'var(--bg-primary)'
                  }}>
                    <Icon size={24} color="var(--text-primary)" />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                    {service.title}
                  </h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Showcase */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '32px'
          }}>
            <div>
              <img
                src="https://images.unsplash.com/photo-1625513123245-fcb02d69ad12?w=600&q=80"
                alt="Optimisation des déplacements"
                style={{
                  width: '100%',
                  height: '280px',
                  objectFit: 'cover',
                  marginBottom: '12px'
                }}
              />
              <p className="body-regular" style={{
                textAlign: 'center',
                color: 'var(--text-primary)',
                fontWeight: 600
              }}>
                {t.services.caption1}
              </p>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/pgvyw04i_JSX%20CONSEILS%20Image%203.jpg"
                alt="Séminaires privés"
                style={{
                  width: '100%',
                  height: '280px',
                  objectFit: 'cover',
                  marginBottom: '12px'
                }}
              />
              <p className="body-regular" style={{
                textAlign: 'center',
                color: 'var(--text-primary)',
                fontWeight: 600
              }}>
                {t.services.caption2}
              </p>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_global-advisory-1/artifacts/ntr0wli4_JSX%20CONSEILS%20Image%204.jpg"
                alt="Réseautage d'Affaires et Accompagnement"
                style={{
                  width: '100%',
                  height: '280px',
                  objectFit: 'cover',
                  marginBottom: '12px'
                }}
              />
              <p className="body-regular" style={{
                textAlign: 'center',
                color: 'var(--text-primary)',
                fontWeight: 600
              }}>
                {t.services.caption3}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;